import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate, formatDateTime } from '../../utils/dateUtils';

interface EDMCampaign {
  _id: string;
  name: string;
  type: string;
  status: string;
  nextSendAt?: string;
  scheduleConfig?: {
    frequency: string;
    startDate: string;
    endDate?: string;
    timeOfDay?: string;
  };
}

export const CampaignCalendar: React.FC = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [campaigns, setCampaigns] = useState<EDMCampaign[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'month' | 'week' | 'day'>('month');

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    // Wait for auth to finish loading, then only fetch if authenticated
    if (authLoading) {
      return; // Still checking auth
    }
    
    if (isAuthenticated && user) {
      fetchCampaigns();
    } else {
      // Not authenticated - set empty state
      setCampaigns([]);
      setLoading(false);
    }
  }, [isAuthenticated, user, authLoading]);

  const fetchCampaigns = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !isAuthenticated || !user) {
        setCampaigns([]);
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${apiUrl}/api/edm/campaigns`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Don't try to parse JSON if it's an auth error
      if (response.status === 401 || response.status === 403) {
        setCampaigns([]);
        setLoading(false);
        return;
      }
      
      if (!response.ok) {
        setCampaigns([]);
        setLoading(false);
        return;
      }
      
      const data = await response.json();
      if (data.success) {
        setCampaigns((data.data || []).filter((c: EDMCampaign) => 
          c.type === 'scheduled' && c.status === 'scheduled'
        ));
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (Date | null)[] = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getCampaignsForDate = (date: Date | null): EDMCampaign[] => {
    if (!date) return [];
    
    return campaigns.filter(campaign => {
      if (!campaign.nextSendAt && !campaign.scheduleConfig?.startDate) return false;
      
      const campaignDate = campaign.nextSendAt 
        ? new Date(campaign.nextSendAt)
        : new Date(campaign.scheduleConfig!.startDate);
      
      return campaignDate.toDateString() === date.toDateString();
    });
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const days = getDaysInMonth(currentDate);
  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (loading) {
    return <div className="text-center py-8">{t('common.loading') || 'Loading...'}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t('edm.calendar.title') || 'Calendar'}
        </h2>
        <div className="flex items-center space-x-2">
          <select
            value={view}
            onChange={(e) => setView(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="month">{t('edm.calendar.month') || 'Month'}</option>
            <option value="week">{t('edm.calendar.week') || 'Week'}</option>
            <option value="day">{t('edm.calendar.day') || 'Day'}</option>
          </select>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        {/* Calendar Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigateMonth('prev')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {monthName}
            </h3>
            <button
              onClick={() => navigateMonth('next')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          <button
            onClick={() => setCurrentDate(new Date())}
            className="px-4 py-2 text-sm text-accent-600 dark:text-accent-400 hover:text-accent-700"
          >
            {t('edm.calendar.today') || 'Today'}
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="p-6">
          <div className="grid grid-cols-7 gap-2 mb-2">
            {dayNames.map(day => (
              <div
                key={day}
                className="text-center text-sm font-medium text-gray-500 dark:text-gray-400 py-2"
              >
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-2">
            {days.map((date, index) => {
              const dayCampaigns = getCampaignsForDate(date);
              const isToday = date && date.toDateString() === new Date().toDateString();
              const isCurrentMonth = date && date.getMonth() === currentDate.getMonth();

              return (
                <div
                  key={index}
                  className={`
                    min-h-24 p-2 border border-gray-200 dark:border-gray-700 rounded-lg
                    ${isToday ? 'bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50 border-blue-300 dark:border-accent-700' : ''}
                    ${!isCurrentMonth ? 'opacity-50' : ''}
                  `}
                >
                  {date && (
                    <>
                      <div className={`text-sm font-medium mb-1 ${isToday ? 'text-accent-600 dark:text-accent-400' : 'text-gray-900 dark:text-white'}`}>
                        {date.getDate()}
                      </div>
                      <div className="space-y-1">
                        {dayCampaigns.slice(0, 2).map(campaign => (
                          <div
                            key={campaign._id}
                            className="text-xs px-2 py-1 bg-accent-100 dark:bg-accent-900 text-accent-800 dark:text-accent-200 rounded truncate"
                            title={campaign.name}
                          >
                            {campaign.name}
                          </div>
                        ))}
                        {dayCampaigns.length > 2 && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            +{dayCampaigns.length - 2} more
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Campaigns List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('edm.calendar.upcomingCampaigns') || 'Upcoming Campaigns'}
          </h3>
        </div>
        <div className="p-6">
          {campaigns.length === 0 ? (
            <p className="text-center text-gray-500 dark:text-gray-400 py-4">
              {t('edm.calendar.noScheduledCampaigns') || 'No scheduled campaigns'}
            </p>
          ) : (
            <div className="space-y-3">
              {campaigns
                .sort((a, b) => {
                  const dateA = a.nextSendAt ? new Date(a.nextSendAt).getTime() : 0;
                  const dateB = b.nextSendAt ? new Date(b.nextSendAt).getTime() : 0;
                  return dateA - dateB;
                })
                .map(campaign => (
                  <div
                    key={campaign._id}
                    className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{campaign.name}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {campaign.nextSendAt
                          ? formatDateTime(campaign.nextSendAt)
                          : campaign.scheduleConfig?.startDate
                          ? formatDateTime(campaign.scheduleConfig.startDate)
                          : 'No date set'}
                      </p>
                    </div>
                    <span className="px-2 py-1 text-xs rounded bg-accent-100 text-accent-800 dark:bg-accent-900 dark:text-accent-200">
                      {campaign.type}
                    </span>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
